import React from 'react';
import ConfidenceBadge from '../../../components/ConfidenceBadge/ConfidenceBadge';
import css from './DeepResearchPanel.module.css';

// ── Inline SVG icons (no dependencies) ──────────────────────────────────

const IconSteps = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const IconAnalysis = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="11" y1="8" x2="11" y2="14" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const IconBadge = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const IconAnswer = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

// Filter type → icon for step cards
const FILTER_ICONS = {
  category: '📁',
  subcategory: '📂',
  price: '£',
  condition: '★',
  sort: '↕',
  keywords: '🔍',
};

// ── Shimmer line (loading indicator for text) ───────────────────────────

const ShimmerLine = ({ width = '60%' }) => <div className={css.shimmerLine} style={{ width }} />;

// ── Step Card ───────────────────────────────────────────────────────────

const StepCard = ({ step, index, isLast, isActive }) => {
  const isDone = !isActive;
  return (
    <div
      className={`${css.stepCard} ${isActive ? css.stepCardActive : ''}`}
      style={{ '--step-index': index }}
    >
      <div className={css.stepHeader}>
        <span className={css.stepIcon}>{FILTER_ICONS[step.filter] || '·'}</span>
        <span className={css.stepNumber}>{step.step_number}</span>
        {isDone && <span className={css.stepCheck}>✓</span>}
        {isActive && <span className={css.stepSpinner} />}
      </div>
      <p className={css.stepLabel}>{step.label}</p>
    </div>
  );
};

// ── Annotation Card ─────────────────────────────────────────────────────

const AnnotationCard = ({ annotation, index }) => (
  <div className={css.annotationCard} style={{ '--ann-index': index }}>
    <span className={css.annotationBadge}>{annotation.badge}</span>
    <p className={css.annotationReason}>{annotation.reason}</p>
  </div>
);

// ── Main Panel ──────────────────────────────────────────────────────────

const DeepResearchPanel = ({
  isResearching = false,
  steps = [],
  annotations = [],
  summary = null,
  confidence = null,
  explanation = null,
  error = null,
  phase = 'starting',
  query = '',
  onDismiss,
}) => {
  // Don't render unless research is active or has results
  const hasContent = isResearching || summary || error || steps.length > 0;
  if (!hasContent) return null;

  const isComplete = phase === 'complete';
  const isError = phase === 'error';
  const showAnalysis = ['analysing', 'annotating', 'complete'].includes(phase);
  const showAnnotations = annotations.length > 0;
  const showSummary = !!summary;

  return (
    <div className={`${css.root} ${isError ? css.rootError : ''}`}>
      {/* ── Header ─────────────────────────────────── */}
      <div className={css.header}>
        <div className={css.headerLeft}>
          <span className={css.headerIcon}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <span className={css.headerLabel}>Deep Research</span>
          {isResearching && <span className={css.liveIndicator} />}
        </div>
        {(isComplete || isError) && onDismiss && (
          <button className={css.dismiss} onClick={onDismiss} type="button">
            Dismiss
          </button>
        )}
      </div>

      {/* ── Query ──────────────────────────────────── */}
      {query && <h2 className={css.query}>{query}</h2>}

      {/* ── Steps Section ──────────────────────────── */}
      {steps.length > 0 && (
        <div className={css.section}>
          <h3 className={css.sectionTitle}>
            <IconSteps />
            <span>Steps</span>
            <span className={css.sectionCount}>{steps.length}</span>
          </h3>
          <div className={css.stepsRow}>
            {steps.map((step, i) => (
              <StepCard
                key={i}
                step={step}
                index={i}
                isLast={i === steps.length - 1}
                isActive={isResearching && i === steps.length - 1 && phase === 'filtering'}
              />
            ))}
            {/* Ghost card for next step while still filtering */}
            {isResearching && phase === 'filtering' && steps.length > 0 && (
              <div className={css.stepCardGhost}>
                <ShimmerLine width="80%" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Starting state (no steps yet) ──────────── */}
      {isResearching && steps.length === 0 && (
        <div className={css.section}>
          <h3 className={css.sectionTitle}>
            <IconSteps />
            <span>Steps</span>
          </h3>
          <div className={css.stepsRow}>
            <div className={css.stepCardGhost}>
              <ShimmerLine width="70%" />
            </div>
            <div className={css.stepCardGhost}>
              <ShimmerLine width="55%" />
            </div>
            <div className={css.stepCardGhost}>
              <ShimmerLine width="65%" />
            </div>
          </div>
        </div>
      )}

      {/* ── Analysis Phase ─────────────────────────── */}
      {showAnalysis && (
        <div className={css.section}>
          <h3 className={css.sectionTitle}>
            <IconAnalysis />
            <span>
              {phase === 'analysing'
                ? 'Analysing results...'
                : phase === 'annotating'
                ? 'Picking top listings...'
                : 'Analysis'}
            </span>
            {(phase === 'analysing' || phase === 'annotating') && (
              <span className={css.phaseSpinner} />
            )}
          </h3>

          {/* In-progress shimmer */}
          {phase === 'analysing' && (
            <div className={css.analysisSkeleton}>
              <ShimmerLine width="90%" />
              <ShimmerLine width="75%" />
              <ShimmerLine width="40%" />
            </div>
          )}
        </div>
      )}

      {/* ── Top Picks (Annotations) ────────────────── */}
      {showAnnotations && (
        <div className={css.section}>
          <h3 className={css.sectionTitle}>
            <IconBadge />
            <span>Top Picks</span>
            <span className={css.sectionCount}>{annotations.length}</span>
          </h3>
          <div className={css.annotationsRow}>
            {annotations.map((ann, i) => (
              <AnnotationCard key={ann.listing_id} annotation={ann} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* ── Answer (Summary) ───────────────────────── */}
      {showSummary && (
        <div className={css.section}>
          <h3 className={css.sectionTitle}>
            <IconAnswer />
            <span>Answer</span>
            {confidence != null && <ConfidenceBadge score={confidence} />}
          </h3>
          <p className={css.summaryText}>{summary}</p>
          {explanation && (
            <p className={css.evidenceText}>{explanation}</p>
          )}
        </div>
      )}

      {/* ── Error State ────────────────────────────── */}
      {isError && (
        <div className={css.errorBox}>
          <p className={css.errorText}>Research failed: {error}</p>
        </div>
      )}
    </div>
  );
};

export default DeepResearchPanel;
